/**
 * Screen Ripple Effect
 * 
 * Post-process shader for shockwave distortion effects.
 * Extracted from ImpactEffectManager for single-responsibility design.
 */

import {
  Scene,
  Camera,
  PostProcess,
  Effect,
  Vector3,
} from '@babylonjs/core';
import { logger } from '../../core/Logger';
import { resolveTimeRamp } from './TimeRamps';
import type { TimeRamp, TimeRampPreset } from './TimeRamps';

// Ripple shader code
const RIPPLE_FRAGMENT_SHADER = `
precision highp float;

varying vec2 vUV;
uniform sampler2D textureSampler;

// Ripple parameters
uniform vec2 uCenter;        // Screen-space center (0-1)
uniform float uTime;         // Normalized time (0-1)
uniform float uAmplitude;    // Maximum distortion
uniform float uFrequency;    // Number of wave rings
uniform float uSpeed;        // Expansion speed
uniform float uIntensity;    // Current intensity from time ramp

void main() {
    // When intensity is 0 (no effect active), show normal scene
    if (uIntensity < 0.001) {
        gl_FragColor = texture2D(textureSampler, vUV);
        return;
    }
    
    // Calculate distance from center
    vec2 toCenter = vUV - uCenter;
    float dist = length(toCenter);
    
    // Calculate ripple wave based on distance and time
    float wave = sin(dist * uFrequency * 6.28318 - uTime * uSpeed * 6.28318) * uAmplitude;
    
    // Apply intensity falloff
    wave *= uIntensity;
    
    // Apply radial falloff (effect weakens further from center)
    float falloff = 1.0 - smoothstep(0.0, 0.5, dist);
    wave *= falloff;
    
    // Distort UV coordinates radially
    vec2 dir = normalize(toCenter + vec2(0.0001)); // avoid division by zero
    vec2 distortedUV = vUV + dir * wave;
    
    // Sample the texture with distorted coordinates
    gl_FragColor = texture2D(textureSampler, distortedUV);
}
`;

/**
 * Configuration for a ripple effect
 */
export interface RippleEffectConfig {
  /** Screen-space X center (0-1, normalized). 0 = left, 1 = right */
  centerX: number;
  /** Screen-space Y center (0-1, normalized). 0 = bottom, 1 = top */
  centerY: number;
  /** Duration in seconds (default: 0.5) */
  duration?: number;
  /** Maximum distortion amplitude (default: 0.03) */
  amplitude?: number;
  /** Wave frequency - affects ripple ring density (default: 10) */
  frequency?: number;
  /** Speed of ripple expansion (default: 1.5) */
  speed?: number;
  /** Time ramp preset or custom function (default: 'explosion') */
  timeRamp?: TimeRamp | TimeRampPreset;
}

interface ActiveRipple {
  id: string;
  startTime: number;
  durationMs: number;
  config: Required<Omit<RippleEffectConfig, 'timeRamp'>>;
  timeRamp: TimeRamp;
}

const DEFAULT_CONFIG = {
  duration: 0.5,
  amplitude: 0.03,
  frequency: 10,
  speed: 1.5,
};

let rippleIdCounter = 0;

export class ScreenRippleEffect {
  private scene: Scene;
  private camera: Camera;
  
  private ripplePost: PostProcess | null = null;
  private activeRipples: Map<string, ActiveRipple> = new Map();
  
  private currentState = {
    centerX: 0.5,
    centerY: 0.5,
    time: 0,
    amplitude: 0,
    frequency: 10,
    speed: 1.5,
    intensity: 0,
  };

  constructor(scene: Scene, camera: Camera) {
    this.scene = scene;
    this.camera = camera;
    this.initializeShader();
    logger.debug.log('✅ ScreenRippleEffect initialized');
  }

  private initializeShader(): void {
    // Register the shader
    Effect.ShadersStore['rippleFragmentShader'] = RIPPLE_FRAGMENT_SHADER;
    
    // Create post-process and attach to camera permanently
    // Control visibility via intensity uniform
    this.ripplePost = new PostProcess(
      'screenRipple',
      'ripple',
      ['uCenter', 'uTime', 'uAmplitude', 'uFrequency', 'uSpeed', 'uIntensity'],
      null,
      1.0,
      this.camera,
      undefined,
      this.scene.getEngine()
    );
    
    this.ripplePost.onApply = (effect: Effect) => {
      effect.setFloat2('uCenter', this.currentState.centerX, this.currentState.centerY);
      effect.setFloat('uTime', this.currentState.time);
      effect.setFloat('uAmplitude', this.currentState.amplitude);
      effect.setFloat('uFrequency', this.currentState.frequency);
      effect.setFloat('uSpeed', this.currentState.speed);
      effect.setFloat('uIntensity', this.currentState.intensity);
    };
    
    logger.debug.debug('🌊 Ripple shader initialized');
  }

  /**
   * Convert world position to normalized screen-space coordinates
   */
  public worldToScreen(worldPos: Vector3): { x: number; y: number } {
    const viewMatrix = this.camera.getViewMatrix();
    const projMatrix = this.camera.getProjectionMatrix();
    const transform = viewMatrix.multiply(projMatrix);
    
    const clipPos = Vector3.TransformCoordinates(worldPos, transform);
    
    const x = (clipPos.x + 1) / 2;
    const y = (clipPos.y + 1) / 2;
    
    return { 
      x: Math.max(0, Math.min(1, x)), 
      y: Math.max(0, Math.min(1, y)) 
    };
  }

  /**
   * Trigger a ripple effect at the specified screen position
   * @returns Effect ID for potential cancellation
   */
  public trigger(config: RippleEffectConfig): string {
    const id = `ripple_${++rippleIdCounter}_${Date.now()}`;
    
    const resolvedConfig = {
      centerX: config.centerX,
      centerY: config.centerY,
      duration: config.duration ?? DEFAULT_CONFIG.duration,
      amplitude: config.amplitude ?? DEFAULT_CONFIG.amplitude,
      frequency: config.frequency ?? DEFAULT_CONFIG.frequency,
      speed: config.speed ?? DEFAULT_CONFIG.speed,
    };
    
    const ripple: ActiveRipple = {
      id,
      startTime: performance.now(),
      durationMs: resolvedConfig.duration * 1000,
      config: resolvedConfig,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'explosion'),
    };
    
    this.activeRipples.set(id, ripple);
    
    logger.debug.debug(`🌊 Ripple started: ${id} at (${resolvedConfig.centerX.toFixed(2)}, ${resolvedConfig.centerY.toFixed(2)})`);
    
    return id;
  }

  /**
   * Trigger a ripple at a world position
   */
  public triggerAtWorldPosition(worldPos: Vector3, config?: Partial<Omit<RippleEffectConfig, 'centerX' | 'centerY'>>): string {
    const screenPos = this.worldToScreen(worldPos);
    return this.trigger({
      centerX: screenPos.x,
      centerY: screenPos.y,
      ...config,
    });
  }

  /**
   * Cancel a specific ripple effect
   */
  public cancel(id: string): void {
    this.activeRipples.delete(id);
  }

  /**
   * Cancel all active ripples
   */
  public cancelAll(): void {
    this.activeRipples.clear();
    this.currentState.intensity = 0;
  }

  /**
   * Update all active ripples. Call every frame.
   * Uses performance.now() for real-time (unaffected by game time scale)
   */
  public update(_deltaTime: number): void {
    const now = performance.now();
    const completedRipples: string[] = [];
    
    // Track the ripple with highest intensity to display
    let maxIntensity = 0;
    let dominantRipple: ActiveRipple | null = null;
    
    for (const [id, ripple] of this.activeRipples) {
      const elapsed = now - ripple.startTime;
      const progress = Math.min(1, elapsed / ripple.durationMs);
      
      if (progress >= 1) {
        completedRipples.push(id);
        continue;
      }
      
      const intensity = ripple.timeRamp(progress);
      
      if (intensity > maxIntensity) {
        maxIntensity = intensity;
        dominantRipple = ripple;
      }
    }
    
    // Apply dominant ripple to shader state
    if (dominantRipple) {
      const elapsed = now - dominantRipple.startTime;
      const progress = elapsed / dominantRipple.durationMs;
      
      this.currentState.centerX = dominantRipple.config.centerX;
      this.currentState.centerY = dominantRipple.config.centerY;
      this.currentState.amplitude = dominantRipple.config.amplitude;
      this.currentState.frequency = dominantRipple.config.frequency;
      this.currentState.speed = dominantRipple.config.speed;
      this.currentState.time = progress;
      this.currentState.intensity = maxIntensity;
    } else {
      // Fade out intensity when no active ripples
      this.currentState.intensity = Math.max(0, this.currentState.intensity - _deltaTime * 5);
    }
    
    // Clean up completed ripples
    for (const id of completedRipples) {
      this.activeRipples.delete(id);
    }
  }

  /**
   * Check if any ripples are active
   */
  public hasActiveEffects(): boolean {
    return this.activeRipples.size > 0;
  }

  /**
   * Get count of active ripples
   */
  public getActiveCount(): number {
    return this.activeRipples.size;
  }

  /**
   * Dispose resources
   */
  public dispose(): void {
    this.cancelAll();
    
    if (this.ripplePost) {
      this.camera.detachPostProcess(this.ripplePost);
      this.ripplePost.dispose(this.camera);
      this.ripplePost = null;
    }
    
    logger.debug.log('🗑️ ScreenRippleEffect disposed');
  }
}
